/**
 * On-screen performance readout (`?perf=1`).
 *
 * This exists because five rounds of diagnosing a stutter from a laptop all
 * found real bugs and none of them was what the player on the actual device
 * was seeing. A harness cannot reproduce someone's phone, their GPU, their
 * refresh rate or their connection; the honest way to close that gap is to let
 * the device report for itself.
 *
 * It shows the four numbers that distinguish the failure modes that have
 * actually happened here, so a screenshot of it is a diagnosis:
 *
 *  - **fps / worst** — is the device simply slow, or is it hitching?
 *  - **stall** — frames over 100 ms, the single-freeze signature (a shader
 *    link, an audio device being built, a chunk arriving over the network).
 *  - **judder** — the share of drawn frames repeating the previous pose. This
 *    is the one that reads as continuous shimmer and that every "worst frame"
 *    metric is blind to; it was 50% before render interpolation.
 *  - **the device line** — renderer, DPR, and whether the driver can compile
 *    shaders in parallel. A software rasteriser behaves nothing like a GPU,
 *    which is how a whole round of measurement went to the wrong place.
 *
 * Render-only and opt-in: it never touches the simulation, and without the
 * query parameter it is not constructed at all.
 */

import type { Renderer } from "../render/scene";

const WINDOW = 120;
/** How much of the start is kept separately (s). */
const OPENING_S = 5;

export class PerfOverlay {
  private readonly el: HTMLElement;
  private readonly gaps: number[] = [];
  private prev: { x: number; z: number } | null = null;
  private frozen = 0;
  private counted = 0;
  private stalls = 0;
  private last = performance.now();
  private device = "";
  /**
   * The opening seconds, kept separately and never overwritten.
   *
   * The live figures are a rolling window, so by the time anyone looks at the
   * overlay the start — the part being complained about — has scrolled out of
   * it. These hold what happened in the first OPENING_S so that a screenshot
   * taken at any moment still answers "was the beginning bad?".
   */
  private begun = 0;
  private openWorst = 0;
  private openWorstAt = 0;
  private openStalls = 0;

  constructor(root: HTMLElement) {
    this.el = document.createElement("div");
    this.el.id = "perf-overlay";
    root.appendChild(this.el);
  }

  /** Read the GL strings once; they need a live context. */
  private describeDevice(): string {
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl2") ?? c.getContext("webgl");
      if (!gl) return "no webgl";
      const dbg = gl.getExtension("WEBGL_debug_renderer_info");
      const name = String(
        dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER),
      );
      const parallel = gl.getExtension("KHR_parallel_shader_compile") ? "par" : "NO-par";
      // The full ANGLE string is a paragraph; the useful part is the device.
      const short = name.replace(/^ANGLE \(|\)$/g, "").split(",").slice(-2).join(",").trim();
      return `${short} · dpr${(window.devicePixelRatio || 1).toFixed(1)} · ${parallel}`;
    } catch {
      return "device unknown";
    }
  }

  /**
   * Call once per rendered frame, after the renderer has drawn.
   *
   * `moving` comes from the simulation, not from the drawn positions: a robot
   * standing still draws the same pose every frame, and counting that as
   * judder reported 23% on a run that was perfectly smooth. Judder is only a
   * question about a robot that is supposed to be moving.
   *
   * `driving` starts the opening window. Timing it from page load measured the
   * asset/shader spike, which the warm-up already covers and which the player
   * never sees; what matters is the first seconds of DRIVING.
   */
  sample(renderer: Renderer, moving: boolean, driving: boolean): void {
    // The tier is part of the diagnosis: 20 fps on the lite tier and 20 fps
    // on the full tier are different problems.
    if (!this.device) this.device = `${this.describeDevice()} · ${renderer.qualityTier}`;
    const now = performance.now();
    const gap = now - this.last;
    this.last = now;
    this.gaps.push(gap);
    if (this.gaps.length > WINDOW) this.gaps.shift();
    if (gap > 100) this.stalls++;
    if (driving && this.begun === 0) this.begun = now;
    if (this.begun > 0 && now - this.begun < OPENING_S * 1000) {
      if (gap > this.openWorst) {
        this.openWorst = gap;
        this.openWorstAt = (now - this.begun) / 1000;
      }
      if (gap > 100) this.openStalls++;
    }

    // Judder: did this frame draw the robot where the last one did?
    const p = renderer.drawnRobotPosition();
    if (this.prev && moving) {
      this.counted++;
      if (Math.hypot(p.x - this.prev.x, p.z - this.prev.z) < 1e-7) this.frozen++;
    }
    this.prev = { x: p.x, z: p.z };

    if (this.gaps.length % 15 !== 0) return;
    const sorted = [...this.gaps].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)] ?? 0;
    const worst = sorted[sorted.length - 1] ?? 0;
    const judder = this.counted > 0 ? (100 * this.frozen) / this.counted : 0;
    this.el.textContent =
      `${(1000 / Math.max(median, 0.001)).toFixed(0)} fps · frame ${median.toFixed(0)}/${worst.toFixed(0)} ms\n` +
      `stall ${this.stalls} · judder ${judder.toFixed(0)}%\n` +
      `drive ${OPENING_S}s: worst ${this.openWorst.toFixed(0)}ms @${this.openWorstAt.toFixed(1)}s · stalls ${this.openStalls}\n` +
      this.device;
  }
}
